import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

/**
 * ThemeProvider — system-aware light/dark theme for the ClearOffer app.
 *
 * Design system contract (see DESIGN.md): the light surface is "Paper" (`:root`
 * tokens) and the dark surface is "Ink" (`.dark` tokens). This provider toggles
 * the `dark` class on `document.documentElement` so the whole SPA flips between
 * the two token sets.
 *
 * Behaviour:
 * - Defaults to `system` (follows `prefers-color-scheme`).
 * - Supports manual `light` / `dark` / `system`.
 * - Persists the explicit choice in localStorage.
 * - Reacts live to OS changes while in `system` mode.
 *
 * A tiny inline script in `core/templates/core/index.html` applies the stored
 * choice before the bundle paints to avoid a flash of the wrong theme.
 */

export type Theme = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

const STORAGE_KEY = 'clearoffer-theme';

interface ThemeContextValue {
  /** The user's selected preference (may be `system`). */
  theme: Theme;
  /** The concrete theme currently applied (`system` resolved to light/dark). */
  resolvedTheme: ResolvedTheme;
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

const prefersDark = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-color-scheme: dark)').matches;

const resolveTheme = (theme: Theme): ResolvedTheme => {
  if (theme === 'system') {
    return prefersDark() ? 'dark' : 'light';
  }
  return theme;
};

const readStoredTheme = (): Theme => {
  if (typeof window === 'undefined') return 'system';
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') {
      return stored;
    }
  } catch {
    // localStorage may be unavailable (private mode / SSR); fall through.
  }
  return 'system';
};

const applyResolvedTheme = (resolved: ResolvedTheme): void => {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.classList.toggle('dark', resolved === 'dark');
  root.style.colorScheme = resolved;
};

export const ThemeProvider: React.FC<{
  children: React.ReactNode;
  defaultTheme?: Theme;
}> = ({ children, defaultTheme = 'system' }) => {
  const [theme, setThemeState] = useState<Theme>(() => {
    const stored = readStoredTheme();
    return stored ?? defaultTheme;
  });
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() => resolveTheme(theme));

  // Apply the resolved theme whenever the preference changes.
  useEffect(() => {
    const resolved = resolveTheme(theme);
    setResolvedTheme(resolved);
    applyResolvedTheme(resolved);
  }, [theme]);

  // While in `system` mode, follow live OS preference changes.
  useEffect(() => {
    if (theme !== 'system') return;
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;

    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      const resolved = prefersDark() ? 'dark' : 'light';
      setResolvedTheme(resolved);
      applyResolvedTheme(resolved);
    };
    media.addEventListener('change', handleChange);
    return () => media.removeEventListener('change', handleChange);
  }, [theme]);

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Ignore persistence failures (private mode, etc.).
    }
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({ theme, resolvedTheme, setTheme }),
    [theme, resolvedTheme, setTheme]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = (): ThemeContextValue => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
